/**
 * Dev mailbox (path /dev/mailbox): GET /api/dev/mailbox, a plain list of
 * sign-in code emails for testing. The server 404s this route unless
 * PORTAL_DEV_MAILBOX=1 and PORTAL_MODE=synthetic; that maps to a clear
 * "not available" note rather than a dead screen. Fetches the full list
 * once and filters client-side (server-side `?to=` is an exact match,
 * which is worse UX for a live filter box).
 */
import { useEffect, useState } from 'preact/hooks';
import type { ApiClient } from '../api.js';
import { devMailboxCopy, sharedCopy } from '../copy.js';

type DevMailRow = {
  mailId: number;
  toEmail: string;
  subject: string;
  body: string;
  createdAt: string;
  /** Server-computed code expiry; drives the live countdown line. */
  expiresAt?: string;
};

/** Codes expire in 10 minutes, so a few seconds of lag is harmless; this is
 *  a local synthetic box and the query is a single indexed read. */
const POLL_MS = 3000;

export type DevMailboxProps = { api: ApiClient };

export function DevMailbox({ api }: DevMailboxProps) {
  const [mail, setMail] = useState<DevMailRow[] | null>(null);
  const [notAvailable, setNotAvailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // Re-read on every poll tick so the per-mail countdown moves without new
  // mail arriving; minute granularity, so 3s resolution is plenty.
  const [nowMs, setNowMs] = useState(() => Date.now());

  const load = async (): Promise<void> => {
    setError(null);
    setNotAvailable(false);
    const res = await api.get<{ mail: DevMailRow[] }>('/api/dev/mailbox');
    if (res.kind === 'network-error') {
      setError(sharedCopy.networkError);
      return;
    }
    if (res.status === 404) {
      setNotAvailable(true);
      return;
    }
    if (res.status !== 200) {
      setError(sharedCopy.genericError);
      return;
    }
    setMail(res.body.mail);
  };

  // Poll, don't read once. This screen is opened in a second tab BEFORE the
  // code is requested (that is what the review script tells people to do),
  // so a single mount-time fetch leaves it showing "No mail yet." forever
  // while the row sits in Postgres. Switching browser tabs does not remount
  // a component, so nothing would ever re-read it.
  useEffect(() => {
    let live = true;
    const tick = (): void => {
      if (!live) return;
      setNowMs(Date.now());
      void load();
    };
    tick();
    const timer = setInterval(tick, POLL_MS);
    return () => {
      live = false;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (notAvailable) {
    return (
      <div class="screen">
        <h1>{devMailboxCopy.title}</h1>
        <p>{devMailboxCopy.notAvailable}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div class="screen">
        <p class="field-error" role="alert">
          {error}
        </p>
        <button type="button" class="btn btn--primary" onClick={() => void load()}>
          {sharedCopy.tryAgain}
        </button>
      </div>
    );
  }

  if (!mail) {
    return (
      <p class="loading-notice" role="status">
        {sharedCopy.loading}
      </p>
    );
  }

  const needle = filter.trim().toLowerCase();
  const filtered = needle.length === 0 ? mail : mail.filter((m) => m.toEmail.toLowerCase().includes(needle));

  const expiryNote = (m: DevMailRow): { text: string; expired: boolean } | null => {
    if (!m.expiresAt) return null;
    const msLeft = Date.parse(m.expiresAt) - nowMs;
    if (Number.isNaN(msLeft)) return null;
    if (msLeft <= 0) return { text: devMailboxCopy.codeExpired, expired: true };
    return { text: devMailboxCopy.codeExpiresIn(Math.ceil(msLeft / 60000)), expired: false };
  };

  return (
    <div class="screen">
      <h1>{devMailboxCopy.title}</h1>
      <p class="notice">{devMailboxCopy.demoLabel}</p>

      <label class="field-label" for="dev-mailbox-filter">
        {devMailboxCopy.filterLabel}
      </label>
      <input
        id="dev-mailbox-filter"
        type="text"
        placeholder={devMailboxCopy.filterPlaceholder}
        value={filter}
        onInput={(e) => setFilter((e.target as HTMLInputElement).value)}
      />

      <div class="dev-mailbox-controls">
        <button type="button" class="btn btn--secondary" onClick={() => void load()}>
          {devMailboxCopy.refreshButton}
        </button>
        <span class="dev-mailbox-auto-note">{devMailboxCopy.autoRefreshNote}</span>
      </div>

      {filtered.length === 0 ? (
        <p>{devMailboxCopy.emptyState}</p>
      ) : (
        <ul class="dev-mailbox-list">
          {filtered.map((m) => (
            <li key={m.mailId} class="dev-mailbox-row">
              <p class="dev-mailbox-meta">
                {m.toEmail} · {new Date(m.createdAt).toLocaleString()}
              </p>
              <p class="dev-mailbox-subject">{m.subject}</p>
              <p class="dev-mailbox-body">{m.body}</p>
              {(() => {
                const note = expiryNote(m);
                return note ? (
                  <p class={note.expired ? 'dev-mailbox-expiry dev-mailbox-expiry--expired' : 'dev-mailbox-expiry'}>
                    {note.text}
                  </p>
                ) : null;
              })()}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
